/* SSi website – Methodology (full page).
 *
 * Four progressive-reveal stages:
 *   1. The short version (always open)
 *   2. Going deeper      (unlocked from 1)
 *   3. The crunchy stuff (unlocked from 2)
 *   4. In practice       (unlocked from 3)
 *
 * Each stage is a self-contained chunk. The next stage is hidden
 * behind an "unlock" card; clicking expands the next stage in
 * place with a smooth fade-and-grow, and the unlock card is
 * replaced by the stage content. The page then auto-scrolls so
 * the new stage's heading sits just below the header.
 */

import { Fragment, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

/* -------------------------------------------------------- */
/* Hero                                                      */
/* -------------------------------------------------------- */
function MethHero({ t }) {
  return (
    <section style={mStyles.heroWrap}>
      <div className="ssi-meth__heroInner" style={mStyles.heroInner}>
        <div className="ssi-meth__heroCopy" style={mStyles.heroCopy}>
          <div style={mStyles.heroKicker}>{t.methPageKicker}</div>
          <h1 style={mStyles.heroH1}>{t.methPageTitle}</h1>
          <p className="ssi-meth__heroLede" style={mStyles.heroLede}>{t.methPageLede}</p>
          <div className="ssi-meth__heroSign" style={mStyles.heroSign}>
            <div className="ssi-meth__heroPortrait" style={mStyles.heroPortrait}>
              <div style={mStyles.heroPortraitPanel}/>
              <div style={mStyles.heroPortraitImg} aria-label="Portrait of Aran Jones, founder of SaySomethingin">
                <img src="/assets/team/aran-j.jpg" alt="Aran Jones" style={mStyles.heroPortraitPhoto}/>
              </div>
            </div>
            <div>
              <div style={mStyles.heroSignName}>Aran Jones</div>
              <div style={mStyles.heroSignRole}>Founder, SaySomethingin</div>
            </div>
          </div>
        </div>
        <div className="ssi-meth__heroArt" style={mStyles.heroArt} aria-hidden="true">
          <HeroBubbles/>
        </div>
      </div>
    </section>
  );
}

/* Playful chorus of greetings in the languages we teach. */
function HeroBubbles() {
  const bubbles = [
    { t: 'Helo!',       fill:'var(--brandRed)', color:'#fff',           rot:-8,  x:170, y: 28, w:160, h: 78, fs:30, tail:'bl' },
    { t: '¡Hola!',      fill:'#fff',            color:'var(--brandRed)', rot: 6,  x: 30, y:118, w:180, h: 84, fs:32, tail:'br', stroke:true },
    { t: 'Bore da!',    fill:'var(--brandRed)', color:'#fff',           rot: 4,  x:250, y:148, w:190, h: 84, fs:30, tail:'bl' },
    { t: 'Bonjour!',    fill:'#fff',            color:'var(--fg-1)',     rot:-6,  x:110, y:238, w:200, h: 84, fs:30, tail:'br', stroke:true },
    { t: 'Konnichiwa!', fill:'#fff',            color:'var(--fg-1)',     rot: 8,  x:300, y:268, w:230, h: 84, fs:26, tail:'bl', stroke:true },
    { t: 'Ciao!',       fill:'var(--brandRed)', color:'#fff',           rot:-10, x: 40, y:340, w:140, h: 78, fs:30, tail:'br' },
  ];
  return (
    <svg viewBox="0 0 560 460" style={{width:'100%', height:'auto', display:'block', overflow:'visible'}}>
      <g stroke="var(--brandRed)" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity=".55">
        <path d="M 24 70 q 14 -10 28 0 t 28 0"/>
        <path d="M 470 60 q 12 -10 24 0 t 24 0"/>
        <path d="M 470 410 q 12 -10 24 0 t 24 0"/>
      </g>
      <g fill="var(--brandRed)" opacity=".8">
        <circle cx="500" cy="200" r="3.5"/>
        <circle cx="510" cy="220" r="2"/>
        <circle cx="20" cy="250" r="3"/>
        <circle cx="14" cy="430" r="2.5"/>
      </g>
      {bubbles.map((b, i) => {
        const cx = b.x + b.w/2, cy = b.y + b.h/2;
        const tailY = b.y + b.h - 1;
        const tail = b.tail === 'bl'
          ? `M ${b.x + 26} ${tailY} l -10 22 l 26 -8 z`
          : `M ${b.x + b.w - 26} ${tailY} l 10 22 l -26 -8 z`;
        return (
          <g key={i} transform={`rotate(${b.rot} ${cx} ${cy})`}>
            <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="38" ry="38"
                  fill={b.fill}
                  stroke={b.stroke ? 'var(--border-strong, #1f1f1f)' : 'none'}
                  strokeWidth={b.stroke ? 1.5 : 0}/>
            <path d={tail}
                  fill={b.fill}
                  stroke={b.stroke ? 'var(--border-strong, #1f1f1f)' : 'none'}
                  strokeWidth={b.stroke ? 1.5 : 0}/>
            {b.stroke && (
              <rect x={b.x + (b.tail==='bl' ? 24 : b.w - 52)} y={tailY - 2}
                    width="30" height="3" fill={b.fill}/>
            )}
            <text x={cx} y={cy + b.fs*0.34}
                  textAnchor="middle"
                  fontFamily="var(--font-display)"
                  fontWeight="700"
                  fontSize={b.fs}
                  fill={b.color}>{b.t}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* -------------------------------------------------------- */
/* Stage 1 – The short version                               */
/* -------------------------------------------------------- */
function StageShort({ t }) {
  return (
    <div style={mStyles.stage}>
      <StageHeader
        n="01"
        kicker={t.s1Kicker}
        title={t.s1Title}
      />
      <div style={mStyles.proseInner}>
        <h3 style={mStyles.beatH3Standalone}>{t.s1B1Title}</h3>
        <p style={mStyles.proseLine}>{t.s1B1Body}</p>
        <p style={mStyles.proseLineItalic}>{t.s1B1Body2}</p>
        <p style={mStyles.proseLine}>{t.s1B1Body3}</p>
        <p style={mStyles.proseLineLast}>{t.s1B1Body4}</p>
        <Beat title={t.s1B2Title} body={t.s1B2Body}/>
        <Beat title={t.s1B3Title} body={t.s1B3Body}/>
        <Beat title={t.s1B4Title} body={t.s1B4Body}/>

        <div style={mStyles.pillarsBox}>
          <div style={mStyles.pillarsLabel}>{t.s1PillarsLabel}</div>
          <div className="ssi-meth__pillarsGrid" style={mStyles.pillarsGrid}>
            {[
              ['memories',   t.methMemoriesTitle,   t.s1PMem],
              ['edges',      t.methEdgesTitle,      t.s1PEdg],
              ['mistakes',   t.methMistakesTitle,   t.s1PMis],
              ['confidence', t.methConfidenceTitle, t.s1PCon],
            ].map(([icon, title, body]) =>
              <div key={icon} style={mStyles.pillarItem}>
                <img src={`/assets/icon-${icon}.svg`} alt="" style={mStyles.pillarIcon}/>
                <div style={mStyles.pillarTitle}>{title}</div>
                <div style={mStyles.pillarBody}>{body}</div>
              </div>
            )}
          </div>
        </div>

        <Beat body={t.s1B5Body}/>
        <p style={mStyles.proseClose}>{t.s1Close}</p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- */
/* Stage 2 – Going deeper                                    */
/* -------------------------------------------------------- */
function StageDeeper({ t }) {
  return (
    <div style={mStyles.stage}>
      <StageHeader
        n="02"
        kicker={t.s2Kicker}
        title={t.s2Title}
      />
      <div style={mStyles.proseInner}>
        <Beat title={t.s2B1Title} body={t.s2B1Body}/>
        <p style={mStyles.proseLine}>{t.s2B1Body2}</p>
        <p style={mStyles.proseLine}>{t.s2B1Body3}</p>
        <p style={mStyles.proseLine}><em>{t.s2B1Italic}</em></p>
        <p style={mStyles.proseLine}>{t.s2B1Body4}</p>

        <Pillar n="01" icon="memories" title={t.methMemoriesTitle} sub={t.s2PMemSub}>
          <p>{t.s2PMemP1}</p>
          <p>{t.s2PMemP2}</p>
          <p>{t.s2PMemP3}</p>
        </Pillar>
        <Pillar n="02" icon="edges" title={t.methEdgesTitle} sub={t.s2PEdgSub}>
          <p>{t.s2PEdgP1}</p>
          <p>{t.s2PEdgP2}</p>
          <p>{t.s2PEdgP3}</p>
          <p style={mStyles.aside}>{t.s2PEdgAside}</p>
          <p>{t.s2PEdgP4}</p>
        </Pillar>
        <Pillar n="03" icon="mistakes" title={t.methMistakesTitle} sub={t.s2PMisSub}>
          <p>{t.s2PMisP1}</p>
          <p>{t.s2PMisP2}</p>
          <p style={mStyles.callout}>{t.s2PMisCallout}</p>
          <p>{t.s2PMisP3}</p>
          <p>{t.s2PMisP4}</p>
        </Pillar>
        <Pillar n="04" icon="confidence" title={t.methConfidenceTitle} sub={t.s2PConSub}>
          <p>{t.s2PConP1}</p>
          <p>{t.s2PConP2}</p>
          <p>{t.s2PConP3}</p>
        </Pillar>

        <div style={mStyles.summaryBoxLight}>
          <div style={mStyles.summaryLabelLight}>{t.s2SumLabel}</div>
          <p style={mStyles.summaryBodyLight}>{t.s2SumBody}</p>
        </div>
        <div style={mStyles.summaryBox}>
          <p style={mStyles.summaryBody}>{t.s2SumBody2}</p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- */
/* Stage 3 – The really crunchy stuff                        */
/* -------------------------------------------------------- */
function StageCrunchy({ t }) {
  return (
    <div style={mStyles.stage}>
      <StageHeader
        n="03"
        kicker={t.s3Kicker}
        title={t.s3Title}
      />
      <div style={mStyles.proseInner}>
        <figure style={mStyles.tessQuoteWrap}>
          <div style={mStyles.tessPortrait}>
            <div style={mStyles.tessPortraitPanel}/>
            <div style={mStyles.tessPortraitImg}>
              <img src="/assets/tess-fitzpatrick.png" alt="Prof. Tess Fitzpatrick" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
            </div>
          </div>
          <div style={mStyles.tessRight}>
            <img src="/assets/speech-mark.svg" alt="" style={mStyles.quoteMark}/>
            <blockquote style={mStyles.tessQuote}>{t.s3TessQuote}</blockquote>
            <figcaption style={mStyles.tessAttr}>
              <strong>{t.s3TessName}</strong><br/>
              {t.s3TessRole}
            </figcaption>
          </div>
        </figure>

        <Beat body={t.s3IntroP1}/>
        <p style={mStyles.proseLine}>{t.s3IntroP2}</p>
        <p style={mStyles.proseLine}>{t.s3IntroP3}</p>

        <ResearchBlock
          n="A"
          title={t.s3RABlock}
          body={[t.s3RABody1, t.s3RABody2, t.s3RABody3]}/>

        <ResearchBlock
          n="B"
          title={t.s3RBBlock}
          body={[t.s3RBBody1]}
          bullets={[t.s3RBBul1, t.s3RBBul2, t.s3RBBul3, t.s3RBBul4, t.s3RBBul5]}
          afterBullets={[t.s3RBBody2, t.s3RBBody3]}/>

        <ResearchBlock
          n="C"
          title={t.s3RCBlock}
          body={[t.s3RCBody1, t.s3RCBody2, t.s3RCBody3, t.s3RCBody4]}/>

        <div style={mStyles.objectionBox}>
          <div style={mStyles.objectionLabel}>{t.s3ObjLabel}</div>
          <p style={mStyles.objectionBody}>{t.s3ObjBody1}</p>
          <p style={mStyles.objectionBody}>{t.s3ObjBody2}</p>
        </div>

        <h3 style={mStyles.h3Plain}>{t.s3GapsTitle}</h3>
        <p style={mStyles.proseLine}>{t.s3GapsLede}</p>
        <ul style={mStyles.gapsList}>
          <li style={mStyles.gapsItem}>{t.s3Gap1}</li>
          <li style={mStyles.gapsItem}>{t.s3Gap2}</li>
          <li style={mStyles.gapsItem}>{t.s3Gap3}</li>
        </ul>
        <p style={mStyles.proseLine}>{t.s3GapsClose}</p>

        <h3 style={mStyles.h3Plain}>{t.s3BackTitle}</h3>
        <p style={mStyles.proseLine}>{t.s3BackP1}</p>
        <p style={mStyles.proseLine}>{t.s3BackP2}</p>

        <div style={mStyles.refsWrap}>
          <div style={mStyles.refsSummary}>{t.s3RefsLabel}</div>
          <ol style={mStyles.refsList}>
            {[
              t.s3Ref1, t.s3Ref2, t.s3Ref3, t.s3Ref4, t.s3Ref5,
              t.s3Ref6, t.s3Ref7, t.s3Ref8, t.s3Ref9,
            ].map((r, i) => <li key={i} style={mStyles.refsItem}>{r}</li>)}
          </ol>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- */
/* Stage 4 – In practice                                     */
/* -------------------------------------------------------- */
function StagePractice({ t }) {
  return (
    <div style={mStyles.stage}>
      <StageHeader
        n="04"
        kicker={t.s4Kicker}
        title={t.s4Title}
      />
      <div style={mStyles.proseInner}>
        <h3 style={mStyles.h3Plain}>{t.s4WhyTitle}</h3>
        <Beat body={t.s4WhyP1}/>
        <p style={mStyles.proseLine}>{t.s4WhyP1b}</p>
        <p style={mStyles.proseLine}>{t.s4WhyP2}</p>
        <p style={mStyles.proseLine}>{t.s4WhyP3}</p>
        <p style={mStyles.proseLine}>{t.s4WhyP4}</p>
        <p style={mStyles.proseLine}>{t.s4WhyP5}</p>

        <h3 style={mStyles.h3Plain}>{t.s4ChangedTitle}</h3>
        <p style={mStyles.proseLine}>{t.s4ChangedP1}</p>
        <p style={mStyles.proseLine}>{t.s4ChangedP2}</p>
        <p style={mStyles.proseLine}>{t.s4ChangedP3}</p>
        <p style={mStyles.proseLine}>{t.s4ChangedP4}</p>
        <p style={mStyles.proseLine}>{t.s4ChangedP5}</p>
        <p style={mStyles.proseLine}>{t.s4ChangedP6}</p>
        <p style={mStyles.proseLine}><em>{t.s4ChangedP7}</em></p>

        <h3 style={mStyles.h3Plain}>{t.s4NextTitle}</h3>
        <p style={mStyles.proseLine}>{t.s4NextP1}</p>
        <p style={mStyles.proseLine}>{t.s4NextP2}</p>

        <h3 style={mStyles.h3Plain}>{t.s4ForYouTitle}</h3>
        <p style={mStyles.proseLine}>{t.s4ForYouP1}</p>
        <p style={mStyles.proseLine}>{t.s4ForYouP2}</p>
        <p style={mStyles.proseLine}>{t.s4ForYouP3}</p>

        <div style={mStyles.tryBox}>
          <div style={mStyles.tryKicker}>{t.s4TryKicker}</div>
          <h2 style={mStyles.tryH2}>{t.s4TryTitle}</h2>
          <p style={mStyles.tryBody}>{t.s4TryP1}</p>
          <p style={mStyles.tryBody}>{t.s4TryP1b}</p>
          <p style={mStyles.tryBody}>{t.s4TryP2}</p>
          <p style={mStyles.tryBody}>{t.s4TryP2b}</p>
          <div style={mStyles.tryCtas}>
            <Link to="/#sample" style={mStyles.tryCtaSolid}>{t.s4TryCta1}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- */
/* Reusable bits                                             */
/* -------------------------------------------------------- */
function StageHeader({ n, kicker, title }) {
  return (
    <header style={mStyles.stageHead}>
      <div style={mStyles.stageNum}>{n}</div>
      <div>
        <div style={mStyles.stageKicker}>{kicker}</div>
        <h2 style={mStyles.stageH2}>{title}</h2>
      </div>
    </header>
  );
}

function Beat({ title, body }) {
  return (
    <div style={mStyles.beat}>
      {title && <h3 style={mStyles.beatH3}>{title}</h3>}
      {body && <p style={mStyles.beatP}>{body}</p>}
    </div>
  );
}

function Pillar({ n, icon, title, sub, children }) {
  return (
    <article className="ssi-meth__pillar" style={mStyles.pillarRow}>
      <aside style={mStyles.pillarSide}>
        <div style={mStyles.pillarN}>{n}</div>
        <img src={`/assets/icon-${icon}.svg`} alt="" style={mStyles.pillarRowIcon}/>
        <h3 style={mStyles.pillarRowH3}>{title}</h3>
        <div style={mStyles.pillarRowSub}>{sub}</div>
      </aside>
      <div style={mStyles.pillarBodyCol}>
        {children}
      </div>
    </article>
  );
}

function ResearchBlock({ n, title, body = [], bullets, afterBullets = [] }) {
  return (
    <section style={mStyles.researchRow}>
      <div style={mStyles.researchTag}>{n}</div>
      <div style={mStyles.researchBody}>
        <h3 style={mStyles.researchH3}>{title}</h3>
        {body.map((p, i) => <p key={`b${i}`} style={mStyles.proseLine}>{p}</p>)}
        {bullets && (
          <ol style={mStyles.researchOL}>
            {bullets.map((b, i) => <li key={i} style={mStyles.researchLI}>{b}</li>)}
          </ol>
        )}
        {afterBullets.map((p, i) => <p key={`a${i}`} style={mStyles.proseLine}>{p}</p>)}
      </div>
    </section>
  );
}

function UnlockCard({ n, kicker, line, cta, onUnlock, variant }) {
  const ref = useRef(null);
  if (variant === 'quiet') {
    return (
      <div style={mStyles.unlockQuiet}>
        <button onClick={onUnlock} style={mStyles.unlockQuietBtn} type="button">
          {cta} <span style={mStyles.unlockArrow}>→</span>
        </button>
      </div>
    );
  }
  if (variant === 'curtain') {
    return (
      <div style={mStyles.unlockCurtain} onClick={onUnlock} ref={ref}>
        <div style={mStyles.unlockCurtainInner}>
          <div style={mStyles.unlockN}>{n}</div>
          <div style={mStyles.unlockKicker}>{kicker}</div>
          <div style={mStyles.unlockLine}>{line}</div>
          <div style={mStyles.unlockCta}>
            {cta} <span style={mStyles.unlockArrow}>→</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={mStyles.unlockCard} ref={ref}>
      <div style={mStyles.unlockCardInner}>
        <div style={mStyles.unlockNRow}>
          <div style={mStyles.unlockN}>{n}</div>
          <div style={mStyles.unlockKickerRow}>{kicker}</div>
        </div>
        <div style={mStyles.unlockLineCard}>{line}</div>
        <button onClick={onUnlock} className="btn btn--primary" style={mStyles.unlockBtn} type="button">
          {cta}
          <span style={{marginLeft:8}}>→</span>
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- */
/* Page shell                                                */
/* -------------------------------------------------------- */
export function MethodologyPage({ t, unlockVariant = 'card' }) {
  const [stage, setStage] = useState(1);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const advance = (next) => {
    setStage(next);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = refs[next - 1].current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }));
  };

  const unlockCopy = [
    null,
    { kicker: t.unlock2Kicker, line: t.unlock2Line, cta: t.unlock2Cta, n: '02' },
    { kicker: t.unlock3Kicker, line: t.unlock3Line, cta: t.unlock3Cta, n: '03' },
    { kicker: t.unlock4Kicker, line: t.unlock4Line, cta: t.unlock4Cta, n: '04' },
  ];

  const stages = [
    { node: <StageShort t={t}/>,    bg: '#fff' },
    { node: <StageDeeper t={t}/>,   bg: 'var(--offWhite)' },
    { node: <StageCrunchy t={t}/>,  bg: '#fff' },
    { node: <StagePractice t={t}/>, bg: 'var(--offWhite)' },
  ];

  return (
    <main>
      <MethHero t={t}/>

      {[1, 2, 3, 4].map(i => {
        if (i > stage) return null;
        const nextLocked = (i + 1 > stage) && (i < 4);
        return (
          <Fragment key={i}>
            <section
              ref={refs[i - 1]}
              className="ssi-meth__stageWrap"
              style={{...mStyles.stageWrap, background: stages[i - 1].bg}}
              data-stage-revealed={i === stage && i !== 1 ? 'true' : undefined}
            >
              {stages[i - 1].node}
            </section>
            {nextLocked && (
              <section style={{...mStyles.unlockWrap, background: stages[i - 1].bg}}>
                <UnlockCard
                  variant={unlockVariant}
                  n={unlockCopy[i].n}
                  kicker={unlockCopy[i].kicker}
                  line={unlockCopy[i].line}
                  cta={unlockCopy[i].cta}
                  onUnlock={() => advance(i + 1)}
                />
              </section>
            )}
          </Fragment>
        );
      })}
    </main>
  );
}

/* -------------------------------------------------------- */
/* Styles                                                    */
/* -------------------------------------------------------- */
const mStyles = {
  /* Hero */
  heroWrap: { background:'var(--offWhite)', padding:'88px 32px 96px', borderBottom:'1px solid var(--border)' },
  heroInner: { maxWidth:1180, margin:'0 auto', display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 480px)', gap:64, alignItems:'center' },
  heroCopy: { minWidth:0 },
  heroArt: { minWidth:0, padding:'0 8px' },
  heroKicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  heroH1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(40px, 5.5vw, 72px)', lineHeight:1.02, letterSpacing:'-.02em', margin:'0 0 22px', textWrap:'balance', maxWidth:'18ch' },
  heroLede: { fontSize:21, lineHeight:1.55, color:'var(--grey)', margin:'0 0 40px', maxWidth:'58ch', textWrap:'pretty' },
  heroSign: { display:'flex', alignItems:'center', gap:18 },
  heroPortrait: { position:'relative', width:88, height:88 },
  heroPortraitPanel: { position:'absolute', inset:0, transform:'translate(8px,8px)', background:'var(--brandRed)', borderRadius:'50%', opacity:.18 },
  heroPortraitImg: { position:'relative', width:88, height:88, borderRadius:'50%', overflow:'hidden', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center' },
  heroPortraitPhoto: { width:'100%', height:'100%', objectFit:'cover', objectPosition:'50% 25%', display:'block' },
  heroPortraitPlaceholder: { fontFamily:'var(--font-display)', fontSize:24, color:'var(--brandRed)', fontWeight:700 },
  heroSignName: { fontFamily:'var(--font-display)', fontSize:18, fontWeight:700, color:'var(--fg-1)' },
  heroSignRole: { fontSize:13, color:'var(--grey)' },

  /* Stage */
  stageWrap: { padding:'96px 32px 72px' },
  unlockWrap: { padding:'8px 32px 96px' },
  stage: { maxWidth:880, margin:'0 auto' },
  stageHead: { display:'grid', gridTemplateColumns:'auto 1fr', gap:24, alignItems:'baseline', marginBottom:48, paddingBottom:24, borderBottom:'1px solid var(--border)' },
  stageNum: { fontFamily:'var(--font-display)', fontSize:64, fontWeight:700, color:'var(--brandRed)', lineHeight:.9, letterSpacing:'-.03em' },
  stageKicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  stageH2: { fontFamily:'var(--font-display)', fontSize:'clamp(32px, 4vw, 48px)', lineHeight:1.05, letterSpacing:'-.015em', margin:0, fontWeight:700, textWrap:'balance' },

  /* Prose */
  proseInner: { maxWidth:680, margin:'0 auto' },
  proseLine: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 22px', textWrap:'pretty' },
  proseLineLast: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 32px', textWrap:'pretty' },
  proseLineItalic: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 22px', textWrap:'pretty', fontStyle:'italic' },
  proseLineTight: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:'-12px 0 22px', textWrap:'pretty' },
  proseClose: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:24, lineHeight:1.4, color:'var(--brandRed)', margin:'24px 0 0', textWrap:'balance' },
  beat: { marginBottom:32 },
  beatH3: { fontFamily:'var(--font-display)', fontSize:26, lineHeight:1.15, fontWeight:700, margin:'0 0 12px', letterSpacing:'-.01em', textWrap:'balance' },
  beatH3Standalone: { fontFamily:'var(--font-display)', fontSize:26, lineHeight:1.15, fontWeight:700, margin:'0 0 22px', letterSpacing:'-.01em', textWrap:'balance' },
  beatP: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
  aside: { fontSize:17, lineHeight:1.6, color:'var(--grey)', margin:'0 0 22px', borderLeft:'2px solid var(--border-strong)', paddingLeft:18, fontStyle:'italic' },
  callout: { fontFamily:'var(--font-display)', fontSize:22, lineHeight:1.35, fontWeight:700, color:'var(--brandRed)', margin:'8px 0 22px', textWrap:'balance' },

  /* Stage 1 pillars summary box */
  pillarsBox: { background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:18, padding:'28px 28px 32px', margin:'40px 0' },
  pillarsLabel: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  pillarsGrid: { display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'24px 32px' },
  pillarItem: {},
  pillarIcon: { width:32, height:32, marginBottom:10 },
  pillarTitle: { fontFamily:'var(--font-display)', fontSize:20, fontWeight:700, marginBottom:4, color:'var(--fg-1)' },
  pillarBody: { fontSize:15, lineHeight:1.5, color:'var(--grey)' },

  /* Stage 2 pillar rows */
  pillarRow: { display:'grid', gridTemplateColumns:'220px 1fr', gap:48, padding:'40px 0', borderTop:'1px solid var(--border)' },
  pillarSide: { position:'sticky', top:88, alignSelf:'start' },
  pillarN: { fontFamily:'var(--font-display)', fontSize:40, fontWeight:700, color:'var(--brandRed)', lineHeight:1, letterSpacing:'-.02em', marginBottom:14 },
  pillarRowIcon: { width:40, height:40, marginBottom:10 },
  pillarRowH3: { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, margin:'0 0 6px', letterSpacing:'-.01em' },
  pillarRowSub: { fontSize:14, lineHeight:1.45, color:'var(--grey)', textWrap:'pretty' },
  pillarBodyCol: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)' },

  summaryBoxLight: { background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:18, padding:'32px 36px', margin:'56px 0 16px' },
  summaryLabelLight: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  summaryBodyLight: { fontFamily:'var(--font-display)', fontSize:22, lineHeight:1.4, margin:0, color:'var(--fg-1)', textWrap:'pretty' },
  summaryBox: { background:'var(--brandRed)', color:'#fff', borderRadius:18, padding:'32px 36px', margin:'0' },
  summaryLabel: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'rgba(255,255,255,.85)', fontWeight:700, marginBottom:14 },
  summaryBody: { fontFamily:'var(--font-display)', fontSize:24, lineHeight:1.35, margin:0, textWrap:'pretty', color:'#fff' },

  /* Stage 3 */
  tessQuoteWrap: { display:'grid', gridTemplateColumns:'104px 1fr', gap:28, alignItems:'start', margin:'8px 0 56px', padding:'8px 0', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', paddingTop:32, paddingBottom:32 },
  tessPortrait: { position:'relative', width:104, height:104 },
  tessPortraitPanel: { position:'absolute', inset:0, transform:'translate(8px,8px)', background:'var(--brandRed)', borderRadius:'50%', opacity:.16 },
  tessPortraitImg: { position:'relative', width:104, height:104, borderRadius:'50%', overflow:'hidden', background:'var(--offWhite)', display:'flex', alignItems:'center', justifyContent:'center' },
  tessRight: { position:'relative' },
  quoteMark: { width:36, height:36, opacity:.55, marginBottom:8 },
  tessQuote: { fontFamily:'var(--font-display)', fontSize:24, lineHeight:1.4, color:'var(--fg-1)', fontStyle:'italic', margin:'0 0 16px', textWrap:'pretty' },
  tessAttr: { fontSize:14, lineHeight:1.5, color:'var(--grey)' },

  researchRow: { display:'grid', gridTemplateColumns:'56px 1fr', gap:24, padding:'32px 0', borderTop:'1px solid var(--border)' },
  researchTag: { fontFamily:'var(--font-display)', fontSize:36, fontWeight:700, color:'var(--brandRed)', lineHeight:1 },
  researchBody: {},
  researchH3: { fontFamily:'var(--font-display)', fontSize:24, fontWeight:700, margin:'0 0 14px', letterSpacing:'-.01em', textWrap:'balance' },
  researchOL: { paddingLeft:20, margin:'0 0 22px' },
  researchLI: { fontSize:17, lineHeight:1.6, color:'var(--fg-1)', marginBottom:8, paddingLeft:6 },

  objectionBox: { background:'var(--offWhite)', border:'1px solid var(--border)', borderLeft:'3px solid var(--brandRed)', borderRadius:12, padding:'24px 28px', margin:'32px 0' },
  objectionLabel: { fontFamily:'var(--font-display)', fontSize:18, fontStyle:'italic', color:'var(--brandRed)', marginBottom:10, fontWeight:700 },
  objectionBody: { fontSize:17, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 12px' },

  h3Plain: { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, margin:'40px 0 14px', letterSpacing:'-.01em', textWrap:'balance' },
  gapsList: { paddingLeft:20, margin:'0 0 22px' },
  gapsItem: { fontSize:17, lineHeight:1.6, color:'var(--fg-1)', marginBottom:10, paddingLeft:6 },

  refsWrap: { marginTop:48, paddingTop:32, borderTop:'1px solid var(--border)' },
  refsSummary: { fontSize:13, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700 },
  refsList: { columns:'2', columnGap:32, paddingLeft:18, margin:'18px 0 0', fontSize:13, lineHeight:1.5, color:'var(--grey)' },
  refsItem: { breakInside:'avoid', marginBottom:14 },

  /* Stage 4 */
  tryBox: { background:'var(--brandRed)', color:'#fff', borderRadius:18, padding:'48px 40px', margin:'56px 0 0', textAlign:'center' },
  tryKicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', fontWeight:700, marginBottom:14, color:'rgba(255,255,255,.9)' },
  tryH2: { fontFamily:'var(--font-display)', fontSize:40, lineHeight:1.05, fontWeight:700, color:'#fff', margin:'0 0 16px', textWrap:'balance' },
  tryBody: { fontSize:17, lineHeight:1.55, color:'rgba(255,255,255,.9)', margin:'0 auto 14px', maxWidth:'52ch' },
  tryCtas: { display:'inline-flex', gap:12, marginTop:18, flexWrap:'wrap', justifyContent:'center' },
  tryCtaSolid: { display:'inline-flex', alignItems:'center', justifyContent:'center', background:'#fff', color:'var(--brandRed)', fontWeight:700, fontSize:16, padding:'14px 28px', borderRadius:999, textDecoration:'none', letterSpacing:'.01em', boxShadow:'0 4px 14px rgba(0,0,0,.18)', transition:'transform .15s ease, box-shadow .15s ease' },

  /* Unlock cards */
  unlockCard: { maxWidth:880, margin:'0 auto', borderTop:'1px solid var(--border)', paddingTop:48 },
  unlockCardInner: { background:'#fff', border:'1px solid var(--border)', borderRadius:18, padding:'32px 36px', boxShadow:'var(--shadow-sm)', display:'flex', flexDirection:'column', gap:14, alignItems:'flex-start' },
  unlockNRow: { display:'flex', alignItems:'baseline', gap:16 },
  unlockN: { fontFamily:'var(--font-display)', fontSize:32, fontWeight:700, color:'var(--brandRed)', lineHeight:1, letterSpacing:'-.02em' },
  unlockKickerRow: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--grey)', fontWeight:700 },
  unlockKicker: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  unlockLine: { fontFamily:'var(--font-display)', fontSize:26, lineHeight:1.25, fontWeight:700, marginBottom:18, color:'var(--fg-1)', textWrap:'balance' },
  unlockLineCard: { fontFamily:'var(--font-display)', fontSize:24, lineHeight:1.25, fontWeight:700, color:'var(--fg-1)', textWrap:'balance', maxWidth:'34ch' },
  unlockCta: { fontFamily:'var(--font-body)', fontSize:14, fontWeight:600, color:'var(--brandRed)' },
  unlockBtn: { marginTop:6 },
  unlockArrow: { display:'inline-block', transition:'transform .25s var(--ease-out)' },

  unlockCurtain: { maxWidth:880, margin:'0 auto', cursor:'pointer', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'40px 32px', textAlign:'center', background:'transparent', transition:'background .2s var(--ease-out)' },
  unlockCurtainInner: { display:'flex', flexDirection:'column', alignItems:'center', gap:8 },

  unlockQuiet: { maxWidth:880, margin:'0 auto', textAlign:'center', padding:'24px 0' },
  unlockQuietBtn: { background:'transparent', border:0, color:'var(--brandRed)', fontFamily:'var(--font-body)', fontSize:15, fontWeight:600, cursor:'pointer', padding:'10px 4px', textDecoration:'underline', textUnderlineOffset:6, textDecorationThickness:1 },
};
